import React, { useState, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import {
  useCreateUserAddressMutation,
  useUpdateUserAddressMutation
} from "../../store/services/smApi";
import { MainLayout } from "../../layouts/MainLayout";
import { Container } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setEditableAddress } from "../../store/slices/userSlice";
import * as yup from "yup";
import countryList from "react-select-country-list";
import Breadcrumb from "../../components/Breadcrumb";
import AddressFormFields from "../../components/AddressFormFields";

export const schema = yup.object().shape({
  country: yup.string().required("Country is required"),
  full_name: yup.string().required("Full Name field is required"),
  city: yup.string().required("City field is required"),
  street: yup.string().required("Street field is required"),
  phone: yup.string().required("Phone field is required"),
  building: yup.string().required("Apt, Suite, Other field is required")
});

function AddressForm () {
  const [newAddress, setNewAddress] = useState(null);
  const [createUserAddress, { isLoading: isLoadingCreateUserAddressMutation }] = useCreateUserAddressMutation();
  const [updateUserAddress, { isLoading: isLoadingUpdateUserAddressMutation }] = useUpdateUserAddressMutation();
  const { t } = useTranslation();
  const history = useHistory();
  const editableAddress = useSelector((state) => state.userStore.editableAddress);
  const options = useMemo(() => countryList().getData(), []);
  const dispatch = useDispatch();
  const [crumbs, setCrumbs] = useState([]);

  const handleSubmit = async (values, { setErrors, setStatus, resetForm }) => {
    let result;
    if (editableAddress) {
      result = await updateUserAddress({ body: values, id: editableAddress.id });
    } else {
      result = await createUserAddress({ body: values });
    }
    if (result && result.error) {
      resetForm({ values });
      setErrors(result.error.data);
      setStatus(result.error.data);
    } else if (result) {
      resetForm();
      dispatch(setEditableAddress(null));
      history.push("/account/profile/addresses");
    } else {
      setStatus("Something went wrong");
      resetForm({ values });
    }
  };

  const handleCancel = () => {
    dispatch(setEditableAddress(null));
    history.push("/account/profile/addresses");
  };

  useEffect(() => {
    if (editableAddress) {
      setNewAddress(editableAddress);
    } else {
      setNewAddress({ full_name: "", country: "", city: "", phone: "", street: "", state: "", zip: "", is_default: false });
    }
    setCrumbs([
      { name: "account-info", display: "Account Management" },
      { name: "addresses", display: "Addresses" },
      { name: "address_form", display: editableAddress ? "Edit Address" : "New Address" }
    ]);
  }, []);

  return (
    <MainLayout>
      <div>
        <Container className={"py-4 my-4"}>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <Breadcrumb crumbs={crumbs} basePath="/account/profile" />
              <div className="form-container account-content-container">
                <div className="text-start">
                  <h2 className="text-primary mb-0">{!editableAddress ? t("Add new address") : t("Edit Address")}</h2>
                  <hr className="mb-0" />
                  {newAddress && (
                    <Formik
                      validationSchema={schema}
                      onSubmit={handleSubmit}
                      validateOnChange
                      enableReinitialize
                      initialValues={newAddress}
                    >
                      {(formikProps) => (
                        <AddressFormFields
                          {...formikProps}
                          editableAddress={editableAddress}
                          isLoadingCreateUserAddressMutation={isLoadingCreateUserAddressMutation}
                          isLoadingUpdateUserAddressMutation={isLoadingUpdateUserAddressMutation}
                          onCancel={handleCancel}
                          options={options}
                        />
                      )}
                    </Formik>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </MainLayout>
  );
}

export default AddressForm;
